import React from "react";

export interface BumpAnimationProps {
    /**
     * When `true`, the wrapper div replays the bump keyframe
     * (translateY 0 → +3px → -1px → 0). When `false`, no animation
     * runs — the wrapper renders as a plain pass-through div.
     *
     * To replay on each "trigger" event, the caller should hold a
     * boolean state that flips back to `false` after the duration
     * (or use an animationend listener). React reuses the wrapper
     * div across renders so simply leaving `triggered` true won't
     * replay; the truthy-falsy edge is what mounts the animation.
     */
    triggered: boolean;
    /**
     * Bump duration in ms. Default 200 — matches the v3 stake-flow
     * receiver-card bump.
     */
    duration?: number;
    /**
     * Pass-through className for the wrapper div. The wrapper IS the
     * card / container — replace your existing `<div className=...>`
     * with `<BumpAnimation triggered className=...>`.
     */
    className?: string;
    /**
     * Pass-through inline style. Merged with the animation rule when
     * `triggered` is true.
     */
    style?: React.CSSProperties;
    children: React.ReactNode;
}

const KEYFRAMES_CSS = `
@keyframes bump-anim {
    0%   { transform: translateY(0); }
    45%  { transform: translateY(3px); }
    75%  { transform: translateY(-1px); }
    100% { transform: translateY(0); }
}
`;

/**
 * Bump animation primitive — wraps content with a small
 * translateY-bounce when `triggered` flips to `true`. Designed to
 * be the "receiver" half of the line-drop-then-card-bump entrance:
 * the line drops into the card, and the card bumps as if the line
 * had physical weight.
 *
 * The wrapper div replaces the caller's container — pass your
 * `className` / `style` directly and the animation rule layers on
 * top via the `style` prop.
 */
export function BumpAnimation({
    triggered,
    duration = 200,
    className,
    style,
    children
}: BumpAnimationProps): React.ReactElement {
    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: KEYFRAMES_CSS }} />
            <div
                className={className}
                style={{
                    ...style,
                    animation: triggered
                        ? `bump-anim ${duration}ms ease-out`
                        : style?.animation
                }}
            >
                {children}
            </div>
        </>
    );
}
